import type { ReactNode } from "react";
import { ChevronLeft, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

export function FlowLayout({
  step,
  stepCount,
  title,
  description,
  submitLabel,
  submitting,
  formError,
  onBack,
  onSubmit,
  children,
}: {
  step: number;
  stepCount: number;
  title: string;
  description?: ReactNode;
  submitLabel: string;
  submitting?: boolean;
  formError?: ReactNode;
  onBack: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const first = step === 1;
  return (
    <form
      noValidate
      className="bg-background text-foreground flex min-h-svh flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <header
        className="border-hairline bg-surface/80 sticky top-0 z-30 border-b backdrop-blur-md"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto flex h-14 w-full max-w-xl items-center justify-between px-1">
          <Button type="button" variant="ghost" className="h-11 px-3 text-[15px]" onClick={onBack}>
            {first ? <X className="size-5" /> : <ChevronLeft className="size-5" />}
            {first ? "Cancel" : "Back"}
          </Button>
          <span className="text-muted-foreground tabular pr-3 text-[13px]">
            Step {step} of {stepCount}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label="Progress"
          aria-valuemin={1}
          aria-valuemax={stepCount}
          aria-valuenow={step}
          className="mx-auto flex w-full max-w-xl gap-1.5 px-4 pb-3"
        >
          {Array.from({ length: stepCount }, (_, i) => (
            <span key={i} className={cn("h-1 flex-1 rounded-full transition-colors", i < step ? "bg-foreground" : "bg-muted")} />
          ))}
        </div>
      </header>

      <main className="mx-auto w-full max-w-xl flex-1 px-4 pt-6 pb-8">
        <h1 className="type-title">{title}</h1>
        {description && <p className="text-muted-foreground type-body mt-2">{description}</p>}
        <div className="mt-6">{children}</div>
      </main>

      <footer
        className="border-hairline bg-surface/90 sticky bottom-0 z-30 border-t backdrop-blur-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto w-full max-w-xl space-y-2 px-4 py-3">
          {formError && (
            <p role="alert" className="text-destructive type-footnote">
              {formError}
            </p>
          )}
          <Button type="submit" size="touch" className="w-full" loading={submitting}>
            {submitLabel}
          </Button>
        </div>
      </footer>
    </form>
  );
}
